/**
 * Board replay: the territory list (owner + armies) after applying a game's
 * events to its starting board. Shared by the app's time machine and the
 * server's fog-of-war snapshots (GET /games/:id/snapshot), so both rebuild
 * the board the same way. Moved from apps/mobile/lib/historicalState.ts.
 */
import type { GameEvent, PlayerId, TerritoryId } from './index.js'

export interface HistoricalTerritory {
  id: TerritoryId
  ownerId: PlayerId
  armies: number
}

/**
 * Apply a single event's map effect to a mutable territory map.
 *
 * - `claim` / `placement` / `attack` / `conquest` /
 *   `post-conquest-transfer` / `fortify` / `resign` mutate ownership
 *   and / or armies on one or two territories per event payload.
 * - `turn-start`, `phase-advance`, `card-*`, `elimination`,
 *   `game-end` have no map effect — they're skipped (no-op).
 *
 * Unknown / future event kinds: silently ignored. Forward compat
 * with future kinds that add no map effect is automatic.
 */
export function applyEventToMap(
  byId: Map<TerritoryId, HistoricalTerritory>,
  event: GameEvent,
): void {
  switch (event.kind) {
    case 'claim': {
      const t = byId.get(event.territoryId)
      if (!t) return
      t.ownerId = event.player
      t.armies = event.armies
      return
    }
    case 'placement': {
      for (const p of event.placements) {
        const t = byId.get(p.territoryId)
        if (!t) continue
        t.armies += p.armies
      }
      return
    }
    case 'attack': {
      // The engine applies losses + (on conquest) ownership flip + auto-
      // moved armies atomically inside applyAttack. Mirror that here so
      // a single attack event with conquered=true fully reflects the
      // post-combat board. The matching `conquest` event in the log is
      // then informational only (no-op below).
      const from = byId.get(event.fromTerritoryId)
      const to = byId.get(event.toTerritoryId)
      if (event.conquered) {
        // Number of armies auto-moved into the conquered territory =
        // attacker dice count = roll.attacker.length (engine spec).
        const movedIn = event.roll.attacker.length
        if (from) from.armies -= event.attackerLost + movedIn
        if (to) {
          to.ownerId = event.attacker
          to.armies = movedIn
        }
      } else {
        if (from) from.armies -= event.attackerLost
        if (to) to.armies -= event.defenderLost
      }
      return
    }
    case 'conquest': {
      // Informational only — the `attack` event with conquered=true
      // already applied ownership + army transfer atomically above.
      // Leaving this branch explicit so a future maintainer can't
      // mistake the missing logic for a bug.
      return
    }
    case 'post-conquest-transfer': {
      const from = byId.get(event.fromTerritoryId)
      const to = byId.get(event.toTerritoryId)
      if (from) from.armies -= event.armies
      if (to) to.armies += event.armies
      return
    }
    case 'fortify': {
      const from = byId.get(event.fromTerritoryId)
      const to = byId.get(event.toTerritoryId)
      if (from) from.armies -= event.armies
      if (to) to.armies += event.armies
      return
    }
    case 'resign': {
      // Resigning player's territories revert to neutral (ownerId='');
      // armies stay where they are (engine spec).
      for (const t of byId.values()) {
        if (t.ownerId === event.player) {
          t.ownerId = ''
        }
      }
      return
    }
    // No map effect:
    case 'turn-start':
    case 'phase-advance':
    case 'elimination':
    case 'team-eliminated':
    case 'card-draw':
    case 'card-turn-in':
    case 'game-end':
      return
  }
}

/**
 * Reconstruct the territory list (ownership + armies, by id) after
 * applying events[0..untilEventIndex] inclusive to the initial
 * snapshot.
 *
 * - `untilEventIndex` is the LAST event applied; pass -1 to get the
 *   pristine starting state.
 * - Returns a fresh array — the input snapshot is not mutated.
 * - When `initialTerritories` is undefined (legacy games whose state
 *   predates the snapshot field), returns an empty array. Callers
 *   should detect that and fall back to the live state.
 */
export function reconstructTerritoriesAt(
  initialTerritories: HistoricalTerritory[] | undefined,
  events: GameEvent[] | undefined,
  untilEventIndex: number,
): HistoricalTerritory[] {
  if (!initialTerritories) return []
  // Fresh copies so the input is not mutated.
  const list: HistoricalTerritory[] = initialTerritories.map((t) => ({
    id: t.id,
    ownerId: t.ownerId,
    armies: t.armies,
  }))
  const byId = new Map<TerritoryId, HistoricalTerritory>()
  for (const t of list) byId.set(t.id, t)

  const evs = events ?? []
  const last = Math.min(untilEventIndex, evs.length - 1)
  for (let i = 0; i <= last; i++) {
    const ev = evs[i]
    if (ev) applyEventToMap(byId, ev)
  }
  return list
}
